import { Link } from 'react-router-dom';

import { cn } from '@/utils/cn';

import { Icon } from './Icon';

const TONES = {
  brand: 'bg-brand-50 text-brand-600 ring-brand-100',
  info: 'bg-info-50 text-info-700 ring-info-100',
  success: 'bg-success-50 text-success-700 ring-success-100',
  warning: 'bg-warning-50 text-warning-800 ring-warning-100',
  danger: 'bg-danger-50 text-danger-600 ring-danger-100',
  neutral: 'bg-surface-muted text-ink-muted ring-line',
};

/**
 * One entry in a list of things a person owns or is part of: a job on a
 * profile, a degree, an interview, an offer, an application.
 *
 *   [icon]  Title                          [✎ Edit] [🗑 Delete]
 *           Organisation · Place
 *           📅 Sep 2024 – Present   [Current]
 *           Description, clamped to three lines…
 *
 * The same shape everywhere, so a student moving from Experience to
 * Education to Interviews reads each list the same way. `actions` are the
 * row's labelled small buttons -- Edit as `secondary`, Delete or Remove as
 * `ghostDanger`, each with a 14px icon. On a phone they drop under the text
 * and wrap instead of squeezing it.
 *
 * `footer` is a full-width strip under the row, for something attached to the
 * entry rather than describing it -- the diploma behind a degree.
 */
export function ItemCard({
  icon = 'document',
  tone = 'brand',
  title,
  to,
  subtitle,
  meta,
  metaIcon = 'calendar',
  badges,
  actions,
  children,
  footer,
  className,
  as: Component = 'li',
}) {
  const row = 'flex flex-col gap-3 sm:flex-row sm:items-start';
  const body = (
    <>
      <div className="flex min-w-0 flex-1 gap-3">
        <span
          aria-hidden="true"
          className={cn(
            'grid size-9 shrink-0 place-items-center rounded-lg ring-1 ring-inset',
            TONES[tone] ?? TONES.brand,
          )}
        >
          <Icon name={icon} size={17} />
        </span>

        <div className="min-w-0 flex-1">
          <h3 className="text-ink text-[14px] leading-snug font-semibold">
            {to ? (
              <Link to={to} className="hover:text-brand-700 transition">
                {title}
              </Link>
            ) : (
              title
            )}
          </h3>
          {subtitle && <p className="text-ink-muted text-[12px]">{subtitle}</p>}
          {(meta || badges) && (
            <div className="mt-1.5 flex flex-wrap items-center gap-x-3 gap-y-1">
              {meta && (
                <span className="text-ink-muted inline-flex items-center gap-1.5 text-xs">
                  {metaIcon && <Icon name={metaIcon} size={14} />}
                  {meta}
                </span>
              )}
              {badges}
            </div>
          )}
          {children && (
            <div className="text-ink-muted mt-2 line-clamp-3 text-[12px] leading-5">{children}</div>
          )}
        </div>
      </div>

      {actions && (
        <div className="flex shrink-0 flex-wrap items-center gap-1.5 sm:justify-end">{actions}</div>
      )}
    </>
  );

  return (
    <Component
      className={cn(
        'bg-surface border-line rounded-card shadow-card hover:border-brand-200 group border p-3.5 transition',
        footer ? 'flex flex-col gap-3' : row,
        className,
      )}
    >
      {footer ? (
        <>
          <div className={row}>{body}</div>
          {footer}
        </>
      ) : (
        body
      )}
    </Component>
  );
}
